/**
 * ROUND8: the dog-team gate. Every dog has a handler - a soldier on a beat, the index of his spawn in
 * `SpawnDef.handler`, one dog to a man - and starts at his heel. And the teams do the job they are there
 * for: they work the approach bands the ranger crosses on the way in, never the belt, the compound or the
 * drop band he lands in (docs/milestones/ROUND8-contracts.md §2).
 */

import { isAutomated } from '../../sim/enemies/specials/kinds';
import { DOGS } from '../../sim/tuningDogs';
import type { MissionDef, SpawnDef } from '../../sim/types';
import type { Band } from '../types';

/** How far off his shoulder a dog may start: at heel, and the cell it was rounded to. */
const HEEL_SLACK = 1.5;

const at = (x: number, z: number): string => `${Math.round(x)}, ${Math.round(z)}`;

function inApproach(bands: readonly Band[], z: number): boolean {
  return bands.some((b) => b.kind === 'approach' && z >= b.z0 && z <= b.z1);
}

function handlerFault(def: MissionDef, dog: SpawnDef): string | null {
  const index = dog.handler;
  const where = `the dog at ${at(dog.x, dog.z)}`;
  if (index === undefined || !Number.isInteger(index)) return `${where} has no handler`;
  const man = def.spawns[index];
  if (!man) return `${where} names handler ${index}, who is not on the map`;
  if (man.kind === 'dog' || isAutomated(man.kind) || man.mans !== undefined || man.route !== undefined) {
    return `${where} is handled by a ${man.kind}, not a soldier on foot`;
  }
  if (man.patrol === undefined || man.patrol.length < 2) return `${where} has a handler with no beat to walk`;
  const reach = DOGS.heelDistance + HEEL_SLACK;
  if ((man.x - dog.x) * (man.x - dog.x) + (man.z - dog.z) * (man.z - dog.z) > reach * reach) {
    return `${where} does not start at its handler's heel`;
  }
  return null;
}

export function checkDogs(def: MissionDef, bands: readonly Band[]): string[] {
  const out: string[] = [];
  const handlers = new Set<number>();
  for (const dog of def.spawns) {
    if (dog.kind !== 'dog') continue;
    const fault = handlerFault(def, dog);
    if (fault) {
      out.push(fault);
      continue;
    }
    const index = dog.handler ?? -1;
    if (handlers.has(index)) out.push(`handler ${index} has two dogs`);
    handlers.add(index);
    const beat = def.spawns[index]?.patrol ?? [];
    const astray = [[dog.x, dog.z] as const, ...beat].find(([, z]) => !inApproach(bands, z));
    if (!astray) continue;
    out.push(`the dog team from ${at(dog.x, dog.z)} works ${at(astray[0], astray[1])}, outside the approaches`);
  }
  return out;
}
